/**
 * Main Application Component
 * 
 * Root component that sets up routing and layout structure.
 * Uses TopNav component for global navigation.
 */

import React, { useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { DevModeProvider } from './contexts/DevModeContext';
import { ChatProvider } from './contexts/ChatContext';
import { CollectionProvider } from './contexts/CollectionContext';
import { AuthProvider } from './contexts/AuthContext';
import { PersonaGateProvider } from './contexts/PersonaGateContext';
import TopNav from './components/TopNav';
import ChatPopup from './components/ChatPopup';
import LandingPage from './pages/LandingPage';
import SearchPage from './pages/SearchPage';
import HomePage from './pages/HomePage';
import KinkPage from './pages/KinkPage';
import ReviewPage from './pages/ReviewPage';
import SexActionPage from './pages/SexActionPage';
import ChatPage from './pages/ChatPage';
import ArchetypesPage from './pages/ArchetypesPage';
import ArchetypeDetailPage from './pages/ArchetypeDetailPage';
import GearPage from './pages/GearPage';
import GearDetailPage from './pages/GearDetailPage';
import CharacterCreationPage from './pages/CharacterCreationPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AgeGateModal from './components/AgeGateModal';
import CookieConsent from './components/CookieConsent';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import PageTransition from './components/PageTransition';
import NotFoundPage from './pages/NotFoundPage';
import ScenariosPage from './pages/ScenariosPage';
import ArousalMechanicsPage from './pages/ArousalMechanicsPage';
import ArousalMechanicDetailPage from './pages/ArousalMechanicDetailPage';
import ArousalEffectsPage from './pages/ArousalEffectsPage';
import ArousalEffectDetailPage from './pages/ArousalEffectDetailPage';
import LustWitchPage from './pages/LustWitchPage';
import DiagnosticsPage from './pages/DiagnosticsPage';
import DatabaseBrowsePage from './pages/DatabaseBrowsePage';
import CuratorPage from './pages/CuratorPage';
import PositionDetailPage from './pages/PositionDetailPage';
import DynamicDetailPage from './pages/DynamicDetailPage';
import ScenarioDetailPage from './pages/ScenarioDetailPage';
import BodyPartDetailPage from './pages/BodyPartDetailPage';
import ActivityDetailPage from './pages/ActivityDetailPage';
import AuditReviewPage from './pages/AuditReviewPage';

// Global keyboard shortcut handler hook
function useGlobalKeyboardShortcuts() {
  const navigate = useNavigate();

  const handleKeyDown = useCallback((e) => {
    // Cmd+K or Ctrl+K to open search
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      navigate('/search');
      // Focus the search input after navigation
      setTimeout(() => {
        const searchInput = document.querySelector('input[type="text"]');
        if (searchInput) searchInput.focus();
      }, 100);
    }
  }, [navigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

function AppContent() {
  // Enable global keyboard shortcuts (Cmd+K for search)
  useGlobalKeyboardShortcuts();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-charcoal">
      {/* Age Verification Gate */}
      <AgeGateModal />

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Global Top Navigation */}
      <TopNav />

      {/* Main Content Area - with top padding for fixed nav */}
      <main id="main-content" className="pt-16">
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<PageTransition><LandingPage /></PageTransition>} />
            <Route path="/search" element={<PageTransition><SearchPage /></PageTransition>} />
            <Route path="/browse" element={<PageTransition><HomePage /></PageTransition>} />
            <Route path="/kink/:id" element={<PageTransition><KinkPage /></PageTransition>} />
            <Route path="/review" element={<PageTransition><ReviewPage /></PageTransition>} />
            <Route path="/action/:id" element={<PageTransition><SexActionPage /></PageTransition>} />
            <Route path="/actions" element={<PageTransition><HomePage /></PageTransition>} />
            <Route path="/archetypes" element={<PageTransition><ArchetypesPage /></PageTransition>} />
            <Route path="/archetype/:id" element={<PageTransition><ArchetypeDetailPage /></PageTransition>} />
            <Route path="/gear" element={<PageTransition><GearPage /></PageTransition>} />
            <Route path="/gear/:id" element={<PageTransition><GearDetailPage /></PageTransition>} />

            {/* Auth routes - with wildcards for Clerk nested paths */}
            <Route path="/login" element={<PageTransition><LoginPage /></PageTransition>} />
            <Route path="/login/*" element={<PageTransition><LoginPage /></PageTransition>} />
            <Route path="/register" element={<PageTransition><RegisterPage /></PageTransition>} />
            <Route path="/register/*" element={<PageTransition><RegisterPage /></PageTransition>} />
            <Route path="/profile" element={<PageTransition><ProfilePage /></PageTransition>} />

            {/* Legal pages */}
            <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
            <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />

            {/* Placeholder routes */}
            <Route path="/guides" element={<PageTransition><LandingPage /></PageTransition>} />
            <Route path="/generate" element={<PageTransition><CharacterCreationPage /></PageTransition>} />
            <Route path="/chat" element={<PageTransition><ChatPage /></PageTransition>} />
            <Route path="/scenarios" element={<PageTransition><ScenariosPage /></PageTransition>} />
            <Route path="/mechanics" element={<PageTransition><ArousalMechanicsPage /></PageTransition>} />
            <Route path="/mechanic/:id" element={<PageTransition><ArousalMechanicDetailPage /></PageTransition>} />
            <Route path="/effects" element={<PageTransition><ArousalEffectsPage /></PageTransition>} />
            <Route path="/effect/:id" element={<PageTransition><ArousalEffectDetailPage /></PageTransition>} />
            <Route path="/position/:id" element={<PageTransition><PositionDetailPage /></PageTransition>} />
            <Route path="/dynamic/:id" element={<PageTransition><DynamicDetailPage /></PageTransition>} />
            <Route path="/scenario/:id" element={<PageTransition><ScenarioDetailPage /></PageTransition>} />
            <Route path="/body-part/:id" element={<PageTransition><BodyPartDetailPage /></PageTransition>} />
            <Route path="/activity/:id" element={<PageTransition><ActivityDetailPage /></PageTransition>} />
            <Route path="/lust-witch" element={<PageTransition><LustWitchPage /></PageTransition>} />
            <Route path="/diagnostics" element={<PageTransition><DiagnosticsPage /></PageTransition>} />
            <Route path="/curator" element={<PageTransition><CuratorPage /></PageTransition>} />
            <Route path="/audit" element={<PageTransition><AuditReviewPage /></PageTransition>} />

            {/* Unified Database Browse - /db/:entityType */}
            <Route path="/db/:entityType" element={<PageTransition><DatabaseBrowsePage /></PageTransition>} />

            {/* 404 Catch-all */}
            <Route path="*" element={<PageTransition><NotFoundPage /></PageTransition>} />
          </Routes>
        </AnimatePresence>
      </main>

      {/* AI Chat Popup - persists across navigation */}
      <ChatPopup />

      {/* Global Background Effects */}
      {/* 1. Noise Overlay */}
      <div className="fixed inset-0 pointer-events-none z-[1000] opacity-[0.04] mix-blend-overlay"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")` }}
      />

      {/* 2. Corner Decorations */}
      <div className="fixed top-0 left-0 w-[120px] h-[120px] pointer-events-none opacity-[0.12] z-0"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cpath d='M0,0 L120,0 L120,20 L20,20 L20,120 L0,120 Z' fill='%23E76F81'/%3E%3C/svg%3E")` }}
      />
      <div className="fixed bottom-0 right-0 w-[120px] h-[120px] pointer-events-none opacity-[0.12] z-0"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cpath d='M120,120 L0,120 L0,100 L100,100 L100,0 L120,0 Z' fill='%23D4874E'/%3E%3C/svg%3E")` }}
      />

      {/* 3. Subtle wine glow from bottom */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute bottom-0 left-1/4 right-1/4 h-1/3 bg-gradient-to-t from-wine-950/15 to-transparent blur-3xl" />
      </div>
    </div>
  );
}

function App() {
  return (
    <DevModeProvider>
      <AuthProvider>
        <PersonaGateProvider>
          <CollectionProvider>
            <ChatProvider>
              <AppContent />
            </ChatProvider>
          </CollectionProvider>
        </PersonaGateProvider>
      </AuthProvider>
    </DevModeProvider>
  );
}

export default App;

